import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  Input,
  OnDestroy,
} from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { AudioService } from '@core/services/audio.service';
import { AudioState, IAudioState } from '@models/audio-state';
import {
  FormattedKaraokePlayer,
  IFormattedKaraokePlayer,
  IKaraokeDTO,
  KaraokeDTO,
} from '@models/grade-unit/karaoke';
import { KaraokePlayerService } from '@app/infrastructure/core/services/karaoke-player.service';
import { SelectedGradeStateService } from '@core/services/state/selected-grade-state.service';
import { GradeLevel } from '@models/grade-level';
import { DayBlockService } from '@core/services/api/day-block.service';

@Component({
  selector: 'app-karaoke-player',
  template: `
    <app-karaoke-player-presentation
      appTextSize
      [gradeLevel]="gradeLevel"
      [audio]="audio$.asObservable() | async"
      [currentTime]="currentTime$.asObservable() | async"
      [karaoke]="karaoke$.asObservable() | async"
      (emitLoadAndPlay)="onLoadAndPlay()"
      (emitPause)="onPause()"
      (emitRestart)="onRestart()"
      (emitResume)="onResume()"
      (emitStop)="onStop()"
    ></app-karaoke-player-presentation>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KaraokePlayerSmartComponent implements OnInit, OnDestroy {
  @Input()
  public karaoke: IFormattedKaraokePlayer = new FormattedKaraokePlayer();
  public karaoke$: BehaviorSubject<IFormattedKaraokePlayer> =
    new BehaviorSubject(null);
  @Input()
  public block: KaraokeDTO;

  public audioSrc: string = '';
  public currentTime$ = new BehaviorSubject<number>(0);
  public gradeLevel: GradeLevel;

  private audioObj = new Audio();
  private audioState: IAudioState = new AudioState();
  public audio$: BehaviorSubject<IAudioState> = new BehaviorSubject(
    this.audioState,
  );
  private subscriptions: Subscription[] = [];
  private intervals = [];

  constructor(
    private audioService: AudioService,
    private karaokeService: KaraokePlayerService,
    private gradeState: SelectedGradeStateService,
    private dayBlockService: DayBlockService,
  ) {}

  public ngOnInit(): void {
    if (this.block) {
      this.gradeLevel = this.block.gradeLevel;
      this.karaoke = this.karaokeService.mapToFormattedPlayer(
        this.karaokeService.mapKaraokeDTOToKaraokePlayer(this.block),
      );
      this.karaoke$.next(this.karaoke);
    }

    this.audioSrc = this.karaoke?.audioUrl;
    this.checkForTranscriptionData();
  }
  public ngOnDestroy(): void {
    this.audioService.stop();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    for (const interval of this.intervals) {
      clearInterval(interval);
    }
  }

  public onLoadAndPlay(): void {
    const loadSubscription = this.audioService
      .loadStream(
        this.audio$,
        this.audioObj,
        this.audioState,
        this.currentTime$,
        this.audioSrc,
      )
      .pipe(debounceTime(10))
      .subscribe(() => this.currentTime$.next(this.audioState.currentTime));
    this.subscriptions.push(loadSubscription);
  }

  public onPause(): void {
    this.audioService.pause(this.audioObj);
  }

  public onResume(): void {
    this.audioService.resume(this.audioObj);
  }

  public onRestart(): void {
    this.audioService.restart(this.audioObj);
  }

  public onStop(): void {
    this.audioService.stop();
    this.currentTime$.next(0);
  }

  private checkForTranscriptionData(): void {
    if (this.block.originalTranscription) return;
    const pollingInterval = window.setInterval(() => {
      if (!this.block.id) return;
      this.dayBlockService.getBlockById(this.block.id).subscribe((data) => {
        const karaoke = data as IKaraokeDTO;
        Object.assign(this.block, { ...karaoke });
        this.karaoke = this.karaokeService.mapToFormattedPlayer(
          this.karaokeService.mapKaraokeDTOToKaraokePlayer(this.block),
        );
        this.karaoke$.next(this.karaoke);
        if (this.block.originalTranscription) {
          clearInterval(pollingInterval);
        }
      });
    }, 2000);
    this.intervals.push(pollingInterval);
  }
}
